import type {
  ControlPlaneCapabilities,
  ControlPlaneManagementResponse,
  ControlPlaneOperationalProjection,
} from '@internal/spassu-code-contract';

export const MANAGEMENT_ACTIVE_POLL_INTERVAL_MS = 5_000;
export const MANAGEMENT_STABLE_POLL_INTERVAL_MS = 15_000;

export interface ControlPlaneManagementState {
  activity: ControlPlaneManagementResponse['activity'] | null;
  projection: ControlPlaneOperationalProjection | null;
  loading: boolean;
  stale: boolean;
  error: string | null;
  latestRequestId: number;
  authorizationEpoch: number;
}

export type ControlPlaneManagementAction =
  | { type: 'manual-error'; message: string | null }
  | { type: 'request-started'; requestId: number }
  | {
      type: 'request-succeeded';
      requestId: number;
      response: ControlPlaneManagementResponse;
    }
  | {
      type: 'request-failed';
      requestId: number;
      message: string;
      authorizationDenied: boolean;
    };

export const initialControlPlaneManagementState: ControlPlaneManagementState = {
  activity: null,
  projection: null,
  loading: true,
  stale: false,
  error: null,
  latestRequestId: 0,
  authorizationEpoch: 0,
};

export function controlPlaneManagementReducer(
  state: ControlPlaneManagementState,
  action: ControlPlaneManagementAction,
): ControlPlaneManagementState {
  if (action.type === 'manual-error') {
    return { ...state, error: action.message };
  }
  if (action.requestId < state.latestRequestId) return state;
  if (action.type === 'request-started') {
    return {
      ...state,
      loading: state.projection === null,
      latestRequestId: action.requestId,
    };
  }
  if (action.type === 'request-failed') {
    return reduceManagementFailure(state, action);
  }
  if (action.requestId < state.authorizationEpoch) return state;
  if (isOlderManagementAttempt(state.projection, action.response.projection)) {
    return {
      ...state,
      loading: false,
      latestRequestId: action.requestId,
    };
  }
  return {
    ...state,
    activity: action.response.activity,
    projection: action.response.projection,
    loading: false,
    stale: false,
    error: null,
    latestRequestId: action.requestId,
  };
}

function reduceManagementFailure(
  state: ControlPlaneManagementState,
  action: Extract<ControlPlaneManagementAction, { type: 'request-failed' }>,
): ControlPlaneManagementState {
  const failed = {
    ...state,
    loading: false,
    stale: true,
    error: action.message,
    latestRequestId: action.requestId,
  };
  if (!action.authorizationDenied) return failed;
  return {
    ...failed,
    projection: redactPrivilegedProjection(state.projection),
    authorizationEpoch: action.requestId,
  };
}

export function managementPollDelay(
  projection: ControlPlaneOperationalProjection | null,
): number {
  return projection?.attempt?.state === 'in_progress'
    ? MANAGEMENT_ACTIVE_POLL_INTERVAL_MS
    : MANAGEMENT_STABLE_POLL_INTERVAL_MS;
}

export function isOlderManagementAttempt(
  current: ControlPlaneOperationalProjection | null,
  incoming: ControlPlaneOperationalProjection,
): boolean {
  const previous = current?.attempt;
  const next = incoming.attempt;
  if (!previous || !next || previous.sessionId !== next.sessionId) return false;
  return Date.parse(next.stageChangedAt) < Date.parse(previous.stageChangedAt);
}

function deniedCapabilities(): ControlPlaneCapabilities {
  const denied = {
    allowed: false,
    reasonCode: 'CAPABILITY_UNAVAILABLE',
    reason: 'Autorização indisponível.',
  };
  return {
    pair: denied,
    renew: denied,
    recover: denied,
    resume: denied,
    openCockpit: denied,
    finalize: denied,
    cancel: denied,
    selectForDeletion: denied,
    overrideCleanup: denied,
    enrollCleanup: denied,
    recoverUsage: denied,
  };
}

function redactPrivilegedProjection(
  projection: ControlPlaneOperationalProjection | null,
): ControlPlaneOperationalProjection | null {
  if (!projection) return null;
  return {
    ...projection,
    attempt: projection.attempt
      ? { ...projection.attempt, diagnostic: null }
      : projection.attempt,
    capabilities: deniedCapabilities(),
  };
}
